import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Avatar, Box, Card, CardActionArea, Chip, Typography } from '@mui/material';
import { MdPlace, MdBolt } from 'react-icons/md';
import RunMapView from '@features/mapVisualization/components/RunMapView';
import { classifyRunStopStatus, type RunMapStop } from '@features/mapVisualization/runTypes';
import { LocationPinMarker } from '@components/Maps';
import { useThirdPartyDelivery } from '../thirdParty/useThirdPartyDelivery';
import { deriveTpStatus } from '../thirdParty/statusDerivation';
import { VENDOR_LABELS } from '../thirdParty/types';

interface CourierRunCardProps {
  run: any;
}

/**
 * Runs-list row for a courier-fulfilled run (fulfillment_type external_courier):
 * vendor tag + courier identity in the driver/vehicle slots, live status chip
 * derived from the third-party delivery state, destination + ETA. Uses the
 * standard card layout (the rich ActiveRunCard map stays own-driver-only).
 */
const CourierRunCard: React.FC<CourierRunCardProps> = ({ run }) => {
  const { delivery } = useThirdPartyDelivery(run.id, true);
  const derived = delivery ? deriveTpStatus(delivery.orchestration_status, delivery.delivery_status) : null;

  const destination = run.stops?.[0]?.address || null;
  const courierName = delivery?.driver?.name;
  const vehicle = delivery?.driver?.vehicle;
  const vehicleLabel = vehicle ? [vehicle.make, vehicle.model].filter(Boolean).join(' ') : null;
  const active = derived ? !derived.terminal : false;
  const etaMinutes =
    delivery?.dropoff_eta && active ? Math.max(0, dayjs(delivery.dropoff_eta).diff(dayjs(), 'minute')) : null;

  // Live map for in-flight deliveries (parity with the own-driver ActiveRunCard):
  // destination pin(s) from the run's stops + the courier's polled position.
  const mapStops: RunMapStop[] = (run.stops || [])
    .filter((s: any) => s.address_lat != null && s.address_lng != null)
    .map((s: any) => ({
      id: s.id,
      stopOrder: s.stop_order,
      status: s.status,
      phase: classifyRunStopStatus(s.status),
      address: s.address,
      lat: Number(s.address_lat),
      lng: Number(s.address_lng),
    }));
  const courierPosition =
    delivery?.driver?.lat != null && delivery?.driver?.lng != null
      ? { lat: delivery.driver.lat, lng: delivery.driver.lng }
      : null;
  const mapId = `parts-courier-run-${run.id}`;
  const showMap = active && (mapStops.length > 0 || !!courierPosition);
  const defaultCenter = courierPosition ?? (mapStops[0] ? { lat: mapStops[0].lat, lng: mapStops[0].lng } : undefined);

  return (
    <Card elevation={1} data-testid={`pd-courier-run-${run.id}`} sx={{ overflow: 'hidden' }}>
      <CardActionArea component={Link} to={`/parts-delivery/runs/${run.id}`} sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Courier + vehicle/vendor + destination (leftmost) */}
          <Box sx={{ flex: '2 1 260px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.800' }}>
              <MdBolt size={18} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }} noWrap>
                  {courierName || <em style={{ fontWeight: 400, color: '#9e9e9e' }}>Matching courier…</em>}
                </Typography>
                {active ? (
                  <Box
                    sx={{
                      width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0,
                      animation: 'pd-live 1.6s ease-in-out infinite',
                      '@keyframes pd-live': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
                    }}
                  />
                ) : null}
              </Box>
              <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
                {[vehicleLabel, delivery ? VENDOR_LABELS[delivery.vendor] : 'HopDrive Managed']
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              {destination ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', minWidth: 0 }}>
                  <MdPlace size={12} style={{ flexShrink: 0 }} />
                  <Typography variant='caption' noWrap title={destination}>
                    {destination}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>

          {/* Date/ETA over status pill (right-aligned) */}
          <Box sx={{ flexShrink: 0, ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {etaMinutes !== null
                ? `${etaMinutes} min est. delivery`
                : run.departure_time
                  ? dayjs(run.departure_time).format('M/D h:mm A')
                  : 'On-demand'}
            </Typography>
            <Chip
              label={derived?.label ?? 'Courier'}
              size='small'
              color={derived?.error ? 'error' : derived?.bucket === 'completed' ? 'success' : 'warning'}
              variant='outlined'
              sx={{ fontSize: 11, fontWeight: 600, height: 22 }}
            />
          </Box>
        </Box>
      </CardActionArea>

      {/* Live tracking map (in-flight only) */}
      {showMap ? (
        <Box sx={{ height: 280, position: 'relative' }}>
          <RunMapView mapId={mapId} stops={mapStops} defaultCenter={defaultCenter} defaultZoom={12}>
            {courierPosition ? (
              <LocationPinMarker
                mapId={mapId}
                markerId='driver'
                position={courierPosition}
                hintText={courierName || 'Courier'}
                zIndex={3}
              />
            ) : null}
          </RunMapView>
        </Box>
      ) : null}
    </Card>
  );
};

export default CourierRunCard;
